import React, { useState } from 'react';
import { Loader2, Trash2, Calendar, Tag, Eye, ChevronDown, ChevronUp, Info, Image as ImageIcon, Download } from 'lucide-react';
import DocumentCardRender from './DocumentCardRender';
import {
  getFormatColor,
  detectDocumentFormat,
  getDocumentOpenBehavior
} from '../../utils/documentFormats';
 
const DocumentCard = ({ 
  document, 
  onDelete, 
  onViewDetails, 
  onSelectConcept, 
  onPassageClick,
  isDeleting = false 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Extraction sécurisée des données avec fallbacks
  const {
    id = '',
    analysis = {},
    metadata = {},
    fileUrl = document.metadata?.fileUrl || document.fileUrl || '#',
    matchingPassages = document.matchingPassages || [],
  } = document;

  const title = document.title || 'Document sans titre';
  const summary = analysis.summary || "Pas de résumé disponible";
  const uploadDate = metadata.uploadedAt || new Date().toISOString();
  const concepts = analysis.concepts || [];
  const keywords = analysis.keywords || [];
  const pageCount = metadata.pageCount || metadata.slideCount || '—';
  const language = metadata.language || '—';
  const fileSize = metadata.fileSize;

  // New format-specific metadata
  const hasMultimodal = metadata.visualAnalysis ? true : false;
  const slideCount = metadata.slideCount;
  const sheetCount = metadata.sheetCount;
  const sheetNames = metadata.sheetNames;
  const imageWidth = metadata.imageWidth;
  const imageHeight = metadata.imageHeight;
  const documentType = metadata.documentType;

  // Detect format using smart detection (priority: mimeType > format > fileName)
  const formatInfo = detectDocumentFormat(metadata, document.fileName);
  const formatColor = getFormatColor(formatInfo.category);
  const openBehavior = getDocumentOpenBehavior(formatInfo.mimeType, formatInfo.category);

  const formatFileSize = (size) => {
    if (!size) return '—';
    if (size < 1024) return `${size} o`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} Ko`;
    return `${(size / (1024 * 1024)).toFixed(1)} Mo`;
  };

  const handlePassageClick = (docId, passage) => {
    console.log("DocumentCard: handlePassageClick appelé", {
      docId,
      passage_page: passage?.pageNumber,
      passage_id: passage?.id
    });

    if (onPassageClick) {
      const enhancedPassage = {
        ...passage,
        id: passage.id || `passage-${Date.now()}`,
        pageNumber: passage.pageNumber || 1,
      };

      console.log("DocumentCard: Transmission du passage à onPassageClick", enhancedPassage);
      onPassageClick(docId, enhancedPassage);
      return;
    } else if (onViewDetails) {
      console.log("DocumentCard: onPassageClick non défini, repli sur onViewDetails");
      if (!passage || !passage.pageNumber) {
        onViewDetails(docId);
      } else {
        console.warn("onPassageClick n'est pas défini mais un passage avec numéro de page a été cliqué");
      }
    }
  };

  // Smart document opener based on format
  const handleOpenDocument = (e) => {
    e.preventDefault();

    if (openBehavior === 'viewer') {
      // For PDFs, open PDF viewer directly via onPassageClick
      if (onPassageClick) {
        onPassageClick(id, null); // Pass null for passage to open at page 1
      } else if (onViewDetails) {
        onViewDetails(id);
      } else {
        window.open(fileUrl, '_blank');
      }
    } else if (openBehavior === 'office-viewer') {
      // For Office files, open Office viewer directly via onPassageClick
      if (onPassageClick) {
        onPassageClick(id, null); // Pass null for passage to open Office viewer
      } else if (onViewDetails) {
        onViewDetails(id);
      } else {
        window.open(fileUrl, '_blank');
      }
    } else if (openBehavior === 'newtab') {
      // For images and text files, open in new tab
      window.open(fileUrl, '_blank');
    } else {
      // For other files, trigger download
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = document.fileName || title;
      link.click();
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow overflow-hidden">
      {/* HEADER ULTRA COMPACT - Une ligne */}
      <div className="p-4">
        <div className="flex items-start gap-3">
          {/* Icône du document avec format */}
          <div className="flex-shrink-0 mt-0.5">
            <span className="text-2xl" title={formatInfo.label}>
              {formatInfo.icon}
            </span>
          </div>

          {/* Titre et métadonnées */}
          <div className="flex-1 min-w-0">
            {/* Titre tronqué sur une ligne */}
            <div className="flex items-center gap-2 mb-1.5">
              <h3 className="text-base font-semibold text-gray-900 truncate" title={title}>
                {title}
              </h3>
              <span
                className="px-2 py-0.5 text-xs rounded-full font-medium whitespace-nowrap"
                style={{
                  backgroundColor: `${formatColor}20`,
                  color: formatColor
                }}
              >
                {formatInfo.label}
              </span>
              {hasMultimodal && (
                <span className="px-2 py-0.5 text-xs bg-purple-50 text-purple-700 rounded-full font-medium whitespace-nowrap">
                  ✨ Multimodal
                </span>
              )}
            </div>

            {/* Métadonnées en petite taille */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
              <div className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                <span>{new Date(uploadDate).toLocaleDateString('fr-FR')}</span>
              </div>

              {document.fileName && (
                <div className="flex items-center gap-1">
                  <Tag className="h-3 w-3" />
                  <span className="truncate max-w-[200px]" title={document.fileName}>
                    {document.fileName}
                  </span>
                </div>
              )}

              {/* Indicateur de pages correspondantes */}
              {matchingPassages && matchingPassages.length > 0 && (
                <span className="text-blue-600 font-medium">
                  {matchingPassages.length} page{matchingPassages.length > 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>

          {/* Actions rapides - Icônes simples */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={handleOpenDocument}
              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title={
                openBehavior === 'viewer' ? 'Ouvrir dans le visualiseur PDF' :
                openBehavior === 'office-viewer' ? 'Ouvrir dans le visualiseur Office' :
                openBehavior === 'newtab' ? 'Ouvrir dans un nouvel onglet' :
                'Télécharger le document'
              }
            >
              {openBehavior === 'download' ? (
                <Download className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
            
            {onViewDetails && (
              <button
                onClick={() => onViewDetails(id)}
                className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                title="Voir les détails du document"
              >
                <Info className="h-4 w-4" />
              </button>
            )}
            
            {onDelete && (
              <button
                onClick={() => onDelete(id)}
                disabled={isDeleting}
                className={`p-2 rounded-lg transition-colors ${
                  isDeleting 
                    ? 'text-gray-400 bg-gray-100 cursor-not-allowed' 
                    : 'text-red-500 hover:bg-red-50'
                }`}
                title={isDeleting ? "Suppression en cours..." : "Supprimer"}
              >
                {isDeleting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
              </button>
            )}
          </div>
        </div>

        {/* Résumé - Toujours visible mais compact */}
        <div className="mt-3 ml-8">
          <div className={`${!isExpanded ? 'max-h-16 overflow-hidden' : ''}`}>
            <DocumentCardRender 
              content={summary}
              document={document}
              className={`text-sm text-gray-600 ${!isExpanded ? 'line-clamp-2' : ''}`}
              onPassageClick={handlePassageClick}
            />
          </div>
          
          {summary.length > 150 && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="mt-2 text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center"
            >
              {isExpanded ? 
                <><ChevronUp className="h-3 w-3 mr-1" />Réduire</> : 
                <><ChevronDown className="h-3 w-3 mr-1" />Voir plus</>
              }
            </button>
          )}
        </div>

        {/* Concepts - Compacts et discrets */}
        {concepts.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3 ml-8">
            {concepts.slice(0, isExpanded ? concepts.length : 5).map((concept, idx) => (
              <button
                key={idx}
                onClick={() => onSelectConcept && onSelectConcept(typeof concept === 'object' ? concept.title : concept)}
                className="px-2 py-0.5 text-xs bg-purple-50 text-purple-700 rounded hover:bg-purple-100 transition-colors"
              >
                {typeof concept === 'object' ? concept.title : concept}
              </button>
            ))}
            {!isExpanded && concepts.length > 5 && (
              <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                +{concepts.length - 5}
              </span>
            )}
          </div>
        )}

        {/* Détails étendus */}
        {isExpanded && (
          <div className="mt-4 ml-8 pt-4 border-t border-gray-100 space-y-3">
            {/* Stats du document */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {/* Pages/Slides for documents */}
              {(pageCount !== '—' || slideCount) && (
                <div className="text-sm">
                  <span className="block text-gray-500 text-xs">
                    {slideCount ? 'Slides' : 'Pages'}
                  </span>
                  <span className="font-medium text-gray-900">{slideCount || pageCount}</span>
                </div>
              )}

              {/* Sheets for Excel */}
              {sheetCount && (
                <div className="text-sm">
                  <span className="block text-gray-500 text-xs">Feuilles</span>
                  <span className="font-medium text-gray-900">{sheetCount}</span>
                </div>
              )}

              {/* Image dimensions */}
              {imageWidth && imageHeight && (
                <div className="text-sm">
                  <span className="block text-gray-500 text-xs">Dimensions</span>
                  <span className="font-medium text-gray-900">{imageWidth} × {imageHeight}px</span>
                </div>
              )}

              {/* Document type for images */}
              {documentType && formatInfo.category === 'image' && (
                <div className="text-sm">
                  <span className="block text-gray-500 text-xs">Type</span>
                  <span className="font-medium text-gray-900 capitalize">{documentType}</span>
                </div>
              )}

              {language !== '—' && (
                <div className="text-sm">
                  <span className="block text-gray-500 text-xs">Langue</span>
                  <span className="font-medium text-gray-900">{language}</span>
                </div>
              )}

              <div className="text-sm">
                <span className="block text-gray-500 text-xs">Taille</span>
                <span className="font-medium text-gray-900">{formatFileSize(fileSize)}</span>
              </div>

              <div className="text-sm">
                <span className="block text-gray-500 text-xs">Mots-clés</span>
                <span className="font-medium text-gray-900">{keywords.length}</span>
              </div>
            </div>

            {/* Excel sheet names */}
            {sheetNames && sheetNames.length > 0 && (
              <div>
                <h4 className="text-xs font-medium text-gray-700 mb-2">Feuilles Excel</h4>
                <div className="flex flex-wrap gap-1.5">
                  {sheetNames.map((name, idx) => (
                    <span key={idx} className="px-2 py-0.5 text-xs bg-green-50 text-green-700 rounded">
                      {name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Multimodal Analysis for PowerPoint and Images */}
            {metadata.visualAnalysis && (
              <div className="space-y-3 bg-purple-50 p-3 rounded-lg border border-purple-200">
                <h4 className="text-xs font-semibold text-purple-800 flex items-center gap-1">
                  ✨ Analyse Multimodale
                </h4>

                {/* Images from PowerPoint */}
                {metadata.visualAnalysis.images && metadata.visualAnalysis.images.length > 0 && (
                  <div>
                    <h5 className="text-xs font-medium text-purple-700 mb-2">Images détectées</h5>
                    <div className="space-y-2">
                      {metadata.visualAnalysis.images.slice(0, 3).map((img, idx) => (
                        <div key={idx} className="bg-white p-2 rounded border border-purple-100">
                          <div className="flex items-start gap-2">
                            <ImageIcon className="h-4 w-4 text-purple-600 flex-shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-gray-700">{img.description}</p>
                              {img.page && (
                                <span className="text-xs text-purple-600">Slide {img.page}</span>
                              )}
                              {img.keywords && img.keywords.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {img.keywords.slice(0, 3).map((keyword, kidx) => (
                                    <span key={kidx} className="px-1.5 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">
                                      {keyword}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      {metadata.visualAnalysis.images.length > 3 && (
                        <p className="text-xs text-purple-600">
                          +{metadata.visualAnalysis.images.length - 3} image(s) supplémentaire(s)
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Charts from PowerPoint */}
                {metadata.visualAnalysis.charts && metadata.visualAnalysis.charts.length > 0 && (
                  <div>
                    <h5 className="text-xs font-medium text-purple-700 mb-2">Graphiques détectés</h5>
                    <div className="space-y-2">
                      {metadata.visualAnalysis.charts.slice(0, 2).map((chart, idx) => (
                        <div key={idx} className="bg-white p-2 rounded border border-purple-100">
                          <div className="text-xs">
                            <span className="font-medium text-purple-700">{chart.type}</span>
                            {chart.page && <span className="text-purple-600"> - Slide {chart.page}</span>}
                            <p className="text-gray-700 mt-1">{chart.analysis}</p>
                          </div>
                        </div>
                      ))}
                      {metadata.visualAnalysis.charts.length > 2 && (
                        <p className="text-xs text-purple-600">
                          +{metadata.visualAnalysis.charts.length - 2} graphique(s) supplémentaire(s)
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Tables from PowerPoint */}
                {metadata.visualAnalysis.tables && metadata.visualAnalysis.tables.length > 0 && (
                  <div>
                    <h5 className="text-xs font-medium text-purple-700 mb-2">Tableaux détectés</h5>
                    <div className="space-y-2">
                      {metadata.visualAnalysis.tables.slice(0, 2).map((table, idx) => (
                        <div key={idx} className="bg-white p-2 rounded border border-purple-100">
                          <div className="text-xs">
                            {table.page && <span className="text-purple-600">Slide {table.page}: </span>}
                            <p className="text-gray-700">{table.summary}</p>
                            {table.headers && table.headers.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {table.headers.slice(0, 4).map((header, hidx) => (
                                  <span key={hidx} className="px-1.5 py-0.5 text-xs bg-blue-50 text-blue-700 rounded">
                                    {header}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                      {metadata.visualAnalysis.tables.length > 2 && (
                        <p className="text-xs text-purple-600">
                          +{metadata.visualAnalysis.tables.length - 2} tableau(x) supplémentaire(s)
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Image analysis for standalone images */}
            {formatInfo.category === 'image' && metadata.analysis && (
              <div className="space-y-2 bg-blue-50 p-3 rounded-lg border border-blue-200">
                <h4 className="text-xs font-semibold text-blue-800">Analyse de l'image</h4>

                {metadata.analysis.visualDescription && (
                  <div>
                    <h5 className="text-xs font-medium text-blue-700 mb-1">Description</h5>
                    <p className="text-xs text-gray-700">{metadata.analysis.visualDescription}</p>
                  </div>
                )}

                {metadata.analysis.textContent && metadata.hasText && (
                  <div>
                    <h5 className="text-xs font-medium text-blue-700 mb-1">Texte extrait (OCR)</h5>
                    <p className="text-xs text-gray-700 bg-white p-2 rounded border border-blue-100">
                      {metadata.analysis.textContent}
                    </p>
                  </div>
                )}

                {metadata.analysis.keyConcepts && (
                  <div>
                    <h5 className="text-xs font-medium text-blue-700 mb-1">Concepts clés</h5>
                    <div className="flex flex-wrap gap-1">
                      {metadata.analysis.keyConcepts.split(',').slice(0, 5).map((concept, idx) => (
                        <span key={idx} className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                          {concept.trim()}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {metadata.analysis.importantElements && (
                  <div>
                    <h5 className="text-xs font-medium text-blue-700 mb-1">Éléments importants</h5>
                    <p className="text-xs text-gray-700">{metadata.analysis.importantElements}</p>
                  </div>
                )}
              </div>
            )}

            {/* Pages correspondantes */}
            {matchingPassages && matchingPassages.length > 0 && (
              <div>
                <h4 className="text-xs font-medium text-gray-700 mb-2">Pages correspondantes</h4>
                <div className="flex flex-wrap gap-2">
                  {matchingPassages.map((passage, idx) => (
                    passage.pageNumber ? (
                      <button
                        key={idx}
                        onClick={() => handlePassageClick(id, passage)}
                        className="px-2.5 py-1 bg-yellow-50 text-yellow-700 border border-yellow-200 rounded text-xs hover:bg-yellow-100 transition-colors"
                      >
                        Page {passage.pageNumber}
                      </button>
                    ) : null
                  ))}
                </div>
              </div>
            )}

            {/* Mots-clés détaillés */}
            {keywords.length > 0 && (
              <div>
                <h4 className="text-xs font-medium text-gray-700 mb-2">Mots-clés</h4>
                <div className="flex flex-wrap gap-1.5">
                  {keywords.map((keyword, idx) => (
                    <span key={idx} className="flex items-center px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                      <Tag className="h-3 w-3 mr-1" />
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentCard;